import { Box, Button, CloseButton, Group, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { useAppUpdateBanner } from '@hooks/use-app-update';
import { updateStatus } from '@utils/app-update';

/**
 * Shell-level strip for a tab that has sat on an old build for hours without
 * changing page — the one case the reload on navigation never reaches.
 *
 * It suggests rather than asks: the user reloads when their work is saved, or
 * closes it. Closing it leaves the header's refresh icon behind
 * (AppUpdateHeaderButton), and the next page change still moves the tab on.
 * Same strip anatomy as TrialBanner, on the info tone.
 */
export default function AppUpdateBanner() {
  const { t } = useLanguage();
  const { colors: themeColors } = useTheme();
  const shown = useAppUpdateBanner();
  const [reloading, setReloading] = useState(false);

  if (!shown) return null;
  const tone = themeColors.info;

  const reload = () => {
    setReloading(true);
    window.location.reload();
  };

  return (
    <Box
      role="status"
      data-testid="app-update-banner"
      mb="lg"
      px="md"
      py="xs"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        minHeight: 48,
        background: `linear-gradient(90deg, ${tone}2E 0%, ${tone}12 38%, ${themeColors.card} 78%)`,
        border: `1px solid ${themeColors.border}`,
        borderLeft: `4px solid ${tone}`,
        borderRadius: 8,
      }}
    >
      <IconRefresh size={18} stroke={1.75} style={{ color: tone, flexShrink: 0 }} aria-hidden />
      <Text size="sm" style={{ color: themeColors.text, flex: '1 1 320px', lineHeight: 1.45 }}>
        <Text component="span" fw={600} inherit>
          {t('appUpdate.banner.lead')}
        </Text>{' '}
        <Text component="span" inherit style={{ color: themeColors.textSecondary }}>
          {t('appUpdate.banner.detail')}
        </Text>
      </Text>
      <Group gap="xs" wrap="nowrap" style={{ marginLeft: 'auto' }}>
        <Button size="xs" loading={reloading} leftSection={<IconRefresh size={14} />} onClick={reload}>
          {t('appUpdate.banner.reload')}
        </Button>
        <CloseButton
          size="sm"
          aria-label={t('appUpdate.banner.dismiss')}
          title={t('appUpdate.banner.dismiss')}
          onClick={() => updateStatus.dismiss()}
        />
      </Group>
    </Box>
  );
}
